import api from "@/helpers/api_helper";
import qs from 'qs';

export type CustomerBadge = 'VIP' | 'REPURCHASE' | 'NEW' | 'DORMANT';

export type OrderCustomer = {
    id: string,
    realName: string,
    email: string,
    phoneNumber: string,
    smsMarketingConsented: boolean,
    emailMarketingConsented: boolean,
    userType: '회원' | '비회원' | string,
    totalOrderAmount: number,
    mileageDeposit?: number,
    totalDeposit?: number,
    // 분석 필드
    orderCount?: number,
    lastOrderAt?: string | null,
    avgOrderAmount?: number,
    tags?: string[],
    badges?: CustomerBadge[],
    purchasedProducts?: string[], // 구매 상품(그룹)명 — 단체 발송 상품 필터용
}

export type RepurchaseCycle = {
    productGroupId: string,
    productName: string,
    cycleDays: number | null,
    cycleSource: 'daySupply' | 'interval' | null,
    daysSinceLastPurchase: number,
    daysUntilNextExpected: number | null,
    isRunningOut: boolean,
}

export type PurchaseHistoryItem = {
    mallOrderGroupId: string,
    productName: string,
    optionName?: string,
    orderedAt: string,
    totalCharge: number,
}

export type CustomerDetail = {
    id: string,
    realName: string,
    email: string,
    phoneNumber: string,
    joinedAt: string | null,
    userType: '회원' | '비회원' | string,
    smsMarketingConsented: boolean,
    emailMarketingConsented: boolean,
    totalOrderAmount: number,
    orderCount: number,
    lastOrderAt: string | null,
    firstOrderAt: string | null,
    avgOrderAmount: number,
    totalDeposit: number,
    tags: string[],
    memo: string,
    badges: CustomerBadge[],
    repurchaseCycles: RepurchaseCycle[],
    purchaseHistory: PurchaseHistoryItem[],
}

export const getCustomerList = async (mallId: string, queryParams?: {
    offset: number, limit: number
}): Promise<{
    list: OrderCustomer[],
    total: number;
}> => {

    try {
        const offset = queryParams?.offset || 0;
        const limit = queryParams?.limit || 15;
        queryParams = {
            offset: offset,
            limit: limit,
            ...queryParams,
        }
        const queryStr = qs.stringify(queryParams);
        return await api.get(`/mall/${mallId}/order-customer?${queryStr}`).then(r => {
            return {
                list: r.data?.orderCustomerList || r.data?.list || [],
                total: Number(r?.data?.total || r?.data?.totalCount || 0)
            };
        })
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            }
        } else {
            throw e;
        }
    }
}

export const getCustomerDetail = async (mallId: string, mallUserId: string): Promise<CustomerDetail> => {
    try {
        return await api.get(`/mall/${mallId}/order-customer/${mallUserId}`).then((r) => r.data);
    } catch (e) {
        if (typeof e === 'string') throw { message: e };
        throw e;
    }
};

export const updateCustomerMemo = async (
    mallId: string,
    mallUserId: string,
    memo: string,
): Promise<{ memo: string }> => {
    try {
        return await api.patch(`/mall/${mallId}/order-customer/${mallUserId}/memo`, { memo }).then((r) => r.data);
    } catch (e) {
        if (typeof e === 'string') throw { message: e };
        throw e;
    }
};

export const updateCustomerTags = async (
    mallId: string,
    mallUserId: string,
    tags: string[],
): Promise<{ tags: string[] }> => {
    try {
        return await api.put(`/mall/${mallId}/order-customer/${mallUserId}/tags`, { tags }).then((r) => r.data);
    } catch (e) {
        if (typeof e === 'string') throw { message: e };
        throw e;
    }
};

export type AlimtalkType = 'REPURCHASE' | 'INTAKE';

export type SendAlimtalkBody = {
    type: AlimtalkType,
    customerName?: string,
    productName: string,
    daySupply?: number,
    repurchaseOngoing?: boolean,
    title: string,
    body: string,
};

export type SendBulkAlimtalkBody = {
    type: AlimtalkType,
    mallUserIds: string[],
    productName: string,
    repurchaseOngoing?: boolean,
    title: string,
    body: string,
};

export const sendBulkAlimtalk = async (
    mallId: string,
    body: SendBulkAlimtalkBody,
): Promise<{ total: number; sent: number; skipped: number; creditBalance?: number }> => {
    try {
        return await api
            .post(`/mall/${mallId}/order-customer/alimtalk/bulk`, body)
            .then((r) => r.data);
    } catch (e) {
        if (typeof e === 'string') throw { message: e };
        throw e;
    }
};

export const sendCustomerAlimtalk = async (
    mallId: string,
    mallUserId: string,
    body: SendAlimtalkBody,
): Promise<{ id: string; landingUrl: string; creditBalance?: number }> => {
    try {
        return await api
            .post(`/mall/${mallId}/order-customer/${mallUserId}/alimtalk`, body)
            .then((r) => r.data);
    } catch (e) {
        if (typeof e === 'string') throw { message: e };
        throw e;
    }
};

export type InquiryReplyItem = {
    messageOrder: number,
    body: string,
    attachedFileUrl: string[],
    isInquirer: boolean,
    createdAt: string,
}

export type InquiryItem = {
    id: string,
    title: string,
    inquiryKind: string,
    inquirerMallUserName: string,
    inquiryState?: '미답변' | '답변완료' | string,
    body: string,
    attachedFileUrl?: string[],
    createdAt: string,
    inquiryReplyCount: number,
    inquiryReplyInfo: InquiryReplyItem[]
}
export const getInquiryList = async (mallId:string):Promise<{
    list: InquiryItem[],
}> => {
    try {
        return await api.get(`/mall/${mallId}/inquiry`).then(r => {
            return {
                list: r.data?.inquiryList || [],
            };
        })
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            }
        } else {
            throw e;
        }
    }
}

export type InquiryReplyRequestBody = {
    body: string,
    attachedFileUrl?: string[],
}
export const postInquiryReply = async (mallId:string, inquiryId:string, data:InquiryReplyRequestBody) => {
    try {
        return await api.post(`/mall/${mallId}/inquiry/${inquiryId}/reply`, data).then(r => {
            return r.data || {};
        })
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            }
        } else {
            throw e;
        }
    }
}